/**
 * SSR-Safe Authenticated Fetch Hook for Shopify App Bridge v4
 *
 * This hook provides authenticated fetch for API requests to our backend.
 * It's SSR-safe: returns null during server rendering and initial hydration,
 * then returns a working fetch function once App Bridge is ready.
 *
 * App Bridge v4 loads via script tag and exposes `window.shopify` globally.
 * We poll for its availability rather than using useAppBridge() hook,
 * which can cause hydration mismatches.
 *
 * IMPORTANT: This hook validates that idToken() returns a valid token before
 * marking as ready. This prevents 401 errors when accessing the app outside
 * of Shopify Admin context (e.g., directly at staging URL). If not in a valid
 * Shopify context, the hook will never become ready and will log a warning
 * after the timeout period (3 seconds).
 *
 * Usage:
 * ```tsx
 * const authenticatedFetch = useAuthenticatedFetch();
 *
 * useEffect(() => {
 *   if (!authenticatedFetch) return; // Not ready yet (or not in Shopify context)
 *   authenticatedFetch('/api/data').then(handleResponse);
 * }, [authenticatedFetch]);
 * ```
 */
import { useCallback, useState, useEffect, useRef } from 'react';

type AuthenticatedFetch = ((url: string, options?: RequestInit) => Promise<Response>) | null;

// Maximum time to wait for valid App Bridge context (3 seconds)
const MAX_WAIT_MS = 3000;
const POLL_INTERVAL_MS = 50;

export function useAuthenticatedFetch(): AuthenticatedFetch {
  const [ready, setReady] = useState(false);
  const attemptsRef = useRef(0);
  const maxAttempts = MAX_WAIT_MS / POLL_INTERVAL_MS;

  // Always called - maintains consistent hook count across renders
  useEffect(() => {
    let isMounted = true;

    // Check if App Bridge is loaded AND can provide a valid token
    const checkAppBridge = async (): Promise<boolean> => {
      if (typeof window === 'undefined') {
        return false;
      }

      const shopify = window.shopify;
      if (!shopify) {
        return false;
      }

      try {
        // Verify we can actually get a valid token
        // This fails outside Shopify Admin context
        const token = await shopify.idToken();
        if (token && isMounted) {
          setReady(true);
          return true;
        }
      } catch (err) {
        // idToken() throws when not in proper Shopify context
        // This is expected when accessing staging URL directly - silent after timeout
      }
      return false;
    };

    // Attempt check immediately
    checkAppBridge();

    // Poll for App Bridge availability with attempts limit
    const interval = setInterval(async () => {
      attemptsRef.current++;

      if (attemptsRef.current >= maxAttempts) {
        clearInterval(interval);
        return;
      }

      if (await checkAppBridge()) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [maxAttempts]);

  // Always called - maintains consistent hook count across renders
  const fetchFn = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const shopify = window.shopify;

      if (!shopify) {
        throw new Error('App Bridge not initialized. Ensure the app is loaded in Shopify Admin.');
      }

      // Get session token from App Bridge v4
      const token = await shopify.idToken();

      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    },
    []
  );

  // Return null until App Bridge is ready, then return the fetch function
  // This allows components to show loading state until authenticated requests are possible
  return ready ? fetchFn : null;
}
